import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';

import { PortMessage } from '../../../../types/PortMessage';
import { ProductContext } from '../../../../types/ProductContext';
import { FormikInput } from '../../../cdl/Input/FormikInput';
import { ReadOnlyField } from '../../../cdl/ReadOnlyField/ReadOnlyField';
import { queries } from '../../../common/api/queryKeys/queries';
import { FormikDatePicker } from '../../../common/form-elements/formik/FormikDatePicker';
import { FormikEnumOptionSelect } from '../../../common/form-elements/formik/FormikEnumOptionSelect';
import { FormikFormattedNumberInput } from '../../../common/FormattedNumberInput/FormikFormattedNumberInput';
import { startOfTimeZoneDayAsUtcIso } from '../../../common/helpers/dateFns.helper';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { formatPort } from '../../../common/helpers/formatPort.helper';
import { formatVessel } from '../../../common/helpers/formatVessel.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { Schedule, useSchedules } from '../../../common/hooks/useSchedules';
import { Box } from '../../../common/ui/Box';
import { FormikPortDetailSelect } from '../../../port/portSelect/FormikPortDetailSelect';
import { OfferModel } from '../../model/OfferModel';

export const FormikGeneralInformation = () => {
    const { values: offer, setFieldError, setFieldValue } = useFormikContext<OfferModel>();
    const role = useRole();
    const supplierSettingsQuery = useQuery({
        ...queries.suppliers.detail(offer.supplierId)._ctx.settings,
        enabled: role.isSupplier() && !!offer.supplierId,
    });
    const { schedules } = useSchedules({
        offerId: offer.id,
        scheduleSharingEnabled:
            offer.scheduleSharingEnabled === true && supplierSettingsQuery.data?.scheduleSharingEnabled === true,
    });

    const onPortSelect = (port: PortMessage, scheduleRank?: number) => {
        if (!schedules || !schedules.length) {
            return;
        }

        const matchingSchedule = schedules.find(
            (scheduleEntry: Schedule) => scheduleEntry.port?.id === port.id && scheduleEntry.rank === scheduleRank
        );

        if (!matchingSchedule) {
            return;
        }

        setFieldValue(
            'dateDelivery',
            startOfTimeZoneDayAsUtcIso(matchingSchedule.eta, matchingSchedule.timeZoneId),
            false
        );
        setFieldError('dateDelivery', undefined);
    };

    const showSchedulePortSelect =
        role.isSupplier() &&
        offer.scheduleSharingEnabled === true &&
        supplierSettingsQuery.data?.scheduleSharingEnabled === true;

    return (
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={16}>
            <ReadOnlyField
                label={translate('offer.quotationProcess.labels.customer')}
                value={formatCompanyName({ company: offer.customer }) || ''}
            />

            <ReadOnlyField
                label={translate('offer.quotationProcess.labels.vessel')}
                value={formatVessel({ vessel: offer.vessel }) || ''}
            />

            {showSchedulePortSelect ? (
                <FormikPortDetailSelect
                    name="portId"
                    label={translate('offer.quotationProcess.labels.port')}
                    schedule={schedules}
                    vesselId={offer.vesselId}
                    onSelect={onPortSelect}
                />
            ) : (
                <ReadOnlyField
                    label={translate('offer.quotationProcess.labels.port')}
                    value={formatPort({ port: offer.port }) || ''}
                />
            )}

            <FormikDatePicker name="dateDelivery" label={translate('offer.edit.lubes.deliveryDate')} />

            {offer.isSpot() ? (
                <Box display="grid" gridTemplateColumns="3fr 1fr" gap={8}>
                    <FormikEnumOptionSelect
                        label={translate('offer.edit.lubes.paymentTerms')}
                        name="paymentTermReference"
                        enumType="paymentTermReference"
                        onOptionChange={(option) => {
                            if (option === 'PREPAYMENT') {
                                setFieldValue('paymentTermReferenceDays', undefined);
                            }
                        }}
                    />
                    <FormikFormattedNumberInput
                        name="paymentTermReferenceDays"
                        label={translate('offer.edit.lubes.paymentTermsDays')}
                        allowNegativeValue={false}
                        allowDecimals={false}
                        disabled={offer.paymentTermReference === 'PREPAYMENT'}
                    />
                </Box>
            ) : null}

            <FormikFormattedNumberInput
                name="noticeDays"
                label={translate('offer.edit.lubes.noticeDays')}
                allowNegativeValue={false}
                allowDecimals={false}
                markAsOptional
            />
            <FormikEnumOptionSelect
                name="supplyMode"
                enumType="supplyMode"
                label={translate('offer.edit.lubes.supplyMode')}
                context={ProductContext.LUBES}
            />
            <FormikInput name="vendorReference" label={translate('offer.edit.lubes.vendorReference')} markAsOptional />
        </Box>
    );
};
